import { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import Login from "./Login";
import Home from "./Home";
import Reportes from "./Reportes";
import CargaDispo from "./CargaDispo";
import OfensoresN2 from "./OfensoresN2";
import ResumenICR from "./ResumenICR";
import CausasFalla from "./CausasFalla";
import GestionUsuarios from "./GestionUsuarios";
import PlanesTrabajo from "./PlanesTrabajo";
import SemaforoNodos from "./SemaforoNodos";
import ResetPassword from "./ResetPassword";
import PriorizacionHuella from "./PriorizacionHuella";
import GestionHuella from "./GestionHuella";
import ConciliacionesOfensores from "./ConciliacionesOfensores";
import CalidadNodosHuella from "./CalidadNodosHuella"; // ── NUEVO
import Escalamientos from "./Escalamientos"; // ── NUEVO: Escalamientos (TAS/OT/CHG) de Proyecto Huella
import BacklogR3 from "./BacklogR3"; // ── NUEVO: Backlog OM Fijo — Seguimiento R3
import CargarBacklog from "./CargarBacklog"; // ── NUEVO: Carga masiva Backlog R3 (solo admin)

const TIMEOUT_INACTIVIDAD  = 10 * 60 * 1000;
const PING_INTERVALO       = 60 * 1000;
const VERIFICAR_INTERVALO  = 30 * 1000;

// ─────────────────────────────────────────
// Variable global: persiste entre re-renders del RouteTracker.
// Al cerrar sesión y volver a entrar se reinicia porque el módulo
// no se recarga, pero handleLogout la limpia explícitamente.
// ─────────────────────────────────────────
let _ultimaPaginaGlobal = null;

function RouteTracker({ usuario, onVisit }) {
  const location = useLocation();

  useEffect(() => {
    if (!usuario) return;
    // Solo registra si la página cambió respecto a la última enviada
    if (location.pathname !== _ultimaPaginaGlobal) {
      _ultimaPaginaGlobal = location.pathname;
      onVisit(usuario, location.pathname);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function App() {
  const [usuario, setUsuario] = useState(null);
  const [mostrarAviso, setMostrarAviso] = useState(false);
  const timerInactividad  = useRef(null);
  const timerAviso        = useRef(null);
  const pingInterval      = useRef(null);
  const verificarInterval = useRef(null);

  const cerrarSesionRevocada = useCallback(() => {
    const sesion = sessionStorage.getItem("auth");
    if (sesion) {
      try {
        const u = JSON.parse(sesion);
        fetch(`${process.env.REACT_APP_API_URL}/sesion-ping`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cedula: u.cedula }),
        }).catch(() => {});
      } catch {}
    }
    sessionStorage.removeItem("auth");
    setUsuario(null);
    setMostrarAviso(false);
    alert("⛔ Tu acceso ha sido revocado por el administrador. La sesión ha sido cerrada.");
  }, []);

  const cerrarSesionInactividad = useCallback(() => {
    const sesion = sessionStorage.getItem("auth");
    if (sesion) {
      try {
        const u = JSON.parse(sesion);
        fetch(`${process.env.REACT_APP_API_URL}/sesion-ping`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cedula: u.cedula }),
        }).catch(() => {});
      } catch {}
    }
    sessionStorage.removeItem("auth");
    setUsuario(null);
    setMostrarAviso(false);
  }, []);

  const reiniciarTimer = useCallback(() => {
    if (timerInactividad.current) clearTimeout(timerInactividad.current);
    if (timerAviso.current)       clearTimeout(timerAviso.current);
    setMostrarAviso(false);
    timerAviso.current = setTimeout(() => {
      setMostrarAviso(true);
    }, TIMEOUT_INACTIVIDAD - 60 * 1000);
    timerInactividad.current = setTimeout(() => {
      cerrarSesionInactividad();
    }, TIMEOUT_INACTIVIDAD);
  }, [cerrarSesionInactividad]);

  const enviarPing = useCallback((u) => {
    if (!u) return;
    fetch(`${process.env.REACT_APP_API_URL}/sesion-ping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cedula: u.cedula,
        nombre: u.nombre,
        rol: u.rol,
        pagina: window.location.pathname,
      }),
    }).catch(() => {});
  }, []);

  // ── NUEVO: registra en historial_visitas cada página que abre el usuario ──
  const registrarVisita = useCallback((u, pagina) => {
    if (!u?.cedula) return;
    fetch(`${process.env.REACT_APP_API_URL}/registrar-visita`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cedula: u.cedula, nombre: u.nombre, rol: u.rol, pagina }),
    }).catch(() => {});
  }, []);

  const verificarSesion = useCallback((u, onRevocado) => {
    if (!u?.cedula) return;
    fetch(`${process.env.REACT_APP_API_URL}/verificar-sesion?cedula=${encodeURIComponent(u.cedula)}`)
      .then(r => r.json())
      .then(data => { if (!data.ok) onRevocado(); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!usuario) {
      clearTimeout(timerInactividad.current);
      clearTimeout(timerAviso.current);
      clearInterval(pingInterval.current);
      clearInterval(verificarInterval.current);
      return;
    }
    const eventos = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    eventos.forEach(ev => window.addEventListener(ev, reiniciarTimer));
    reiniciarTimer();
    enviarPing(usuario);
    pingInterval.current = setInterval(() => enviarPing(usuario), PING_INTERVALO);
    verificarSesion(usuario, cerrarSesionRevocada);
    verificarInterval.current = setInterval(
      () => verificarSesion(usuario, cerrarSesionRevocada),
      VERIFICAR_INTERVALO
    );
    return () => {
      eventos.forEach(ev => window.removeEventListener(ev, reiniciarTimer));
      clearTimeout(timerInactividad.current);
      clearTimeout(timerAviso.current);
      clearInterval(pingInterval.current);
      clearInterval(verificarInterval.current);
    };
  }, [usuario, reiniciarTimer, enviarPing, verificarSesion, cerrarSesionRevocada]);

  useEffect(() => {
    const sesion = sessionStorage.getItem("auth");
    if (sesion) {
      try {
        setUsuario(JSON.parse(sesion));
      } catch {
        sessionStorage.removeItem("auth");
      }
    }
  }, []);

  const handleLogin = (data) => {
    setUsuario({
      usuario: data.usuario, nombre: data.nombre, rol: data.rol, cedula: data.cedula,
      aliado: data.aliado, puede_editar: data.puede_editar, modulos_edicion: data.modulos_edicion,
    });
    window.history.replaceState(null, "", "/");
  };

  const handleLogout = () => {
    if (usuario) {
      fetch(`${process.env.REACT_APP_API_URL}/sesion-ping`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cedula: usuario.cedula }),
      }).catch(() => {});
    }
    sessionStorage.removeItem("auth");
    _ultimaPaginaGlobal = null; // Resetear para que al volver a iniciar sesión registre de nuevo
    setUsuario(null);
  };

  const esAdmin = ["administrador"].includes((usuario?.rol || "").toLowerCase());

  return (
    <BrowserRouter>

      {/* ── Aviso cierre por inactividad ── */}
      {mostrarAviso && usuario && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "white", borderRadius: "16px", padding: "32px 36px",
            boxShadow: "0 24px 80px rgba(0,0,0,0.3)", maxWidth: "400px", width: "90%",
            textAlign: "center", fontFamily: "'Segoe UI', Arial, sans-serif",
          }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>⏱️</div>
            <h3 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700" }}>
              Sesión por expirar
            </h3>
            <p style={{ color: "#64748b", fontSize: "14px", margin: "0 0 24px 0", lineHeight: 1.6 }}>
              Tu sesión se cerrará en <strong>1 minuto</strong> por inactividad.<br/>
              ¿Deseas continuar?
            </p>
            <button onClick={reiniciarTimer} style={{
              width: "100%", padding: "12px",
              background: "linear-gradient(135deg, #E30613, #B0000E)",
              color: "white", border: "none", borderRadius: "10px",
              fontSize: "14px", fontWeight: "700", cursor: "pointer",
              boxShadow: "0 4px 16px rgba(227,6,19,0.35)",
            }}>
              ✅ Sí, continuar sesión
            </button>
          </div>
        </div>
      )}

      <Routes>
        {/* Ruta pública */}
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Rutas privadas */}
        <Route path="*" element={
          !usuario ? (
            <Login onLogin={handleLogin} />
          ) : (
            <DashboardLayout usuario={usuario} onLogout={handleLogout}>
              {/* ── Tracker: registra cada cambio de página en historial_visitas ── */}
              <RouteTracker usuario={usuario} onVisit={registrarVisita} />
              <Routes>
                <Route path="/"                    element={<Home               usuario={usuario} />} />
                <Route path="/reportes"            element={<Reportes           usuario={usuario} />} />
                <Route path="/ofensores-n2"        element={<OfensoresN2        usuario={usuario} />} />
                <Route path="/conciliaciones"      element={<ConciliacionesOfensores usuario={usuario} />} />
                <Route path="/resumen-icr"         element={<ResumenICR         usuario={usuario} />} />
                <Route path="/causas-falla"        element={<CausasFalla        usuario={usuario} />} />
                <Route path="/planes"              element={<PlanesTrabajo      usuario={usuario} />} />
                <Route path="/semaforo"            element={<SemaforoNodos      usuario={usuario} />} />
                <Route path="/backlog-r3"          element={<BacklogR3          usuario={usuario} />} /> {/* ── NUEVO */}
                {/* ── Proyecto Huella ── */}
                <Route path="/priorizacion-huella" element={<PriorizacionHuella usuario={usuario} />} />
                <Route path="/calidad-huella"      element={<CalidadNodosHuella usuario={usuario} />} /> {/* ── NUEVO */}
                <Route path="/escalamientos"       element={<Escalamientos      usuario={usuario} />} /> {/* ── NUEVO */}
                {/* ── Solo admin ── */}
                {esAdmin && <Route path="/gestion-huella" element={<GestionHuella usuario={usuario} />} />}
                {esAdmin && <Route path="/cargar"         element={<CargaDispo   usuario={usuario} />} />}
                {esAdmin && <Route path="/cargar-backlog" element={<CargarBacklog usuario={usuario} />} />} {/* ── NUEVO */}
                {esAdmin && <Route path="/usuarios"       element={<GestionUsuarios usuario={usuario} />} />}
                {/* Ruta no definida → Home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </DashboardLayout>
          )
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;